import React, { type FC } from 'react';
import { Box, Heading, Text, TextButton } from '@wix/design-system';
import { ExternalLink } from '@wix/wix-ui-icons-common';
import { tabsByIdMapper } from '../../../utils/mappers';
import { useTranslation } from '@wix/wix-i18n-config';
import { DataHooks } from '../../../constants/dataHooks';
import { DOCS } from '../../../constants/routes';
import { useTemplatesPageBi } from '../../../hooks/useTemplatesPageBi';
import type { TemplateTab } from '../../../types/TemplateTabs';
import { useTabs } from '../../../hooks/useTabs';
import { useExperiments } from '@wix/fe-essentials/react';
import { DevelopmentTool } from '@wix/ambassador-devcenter-apps-v1-app-template/types';
import { BlocksHarmonyNotification } from '../../BlocksHarmonyNotification/BlocksHarmonyNotification';

interface Props {
  activeTabId?: TemplateTab;
}

export const TemplatesGridHeading: FC<Props> = ({
  activeTabId: activeTabIdProp,
}) => {
  const { t } = useTranslation();
  const { activeTabId } = useTabs();
  const { experiments } = useExperiments({ readOnly: true });
  const shouldDisplayNotifications = experiments.enabled(
    'specs.dcc.displayHarmonyNotifications',
  );

  const tab = tabsByIdMapper[activeTabIdProp || activeTabId];
  const { reportClickOnDocs } = useTemplatesPageBi();
  const onDocsLinkClick = () => {
    const docsUrl = DOCS[activeTabIdProp || activeTabId];
    reportClickOnDocs(activeTabId, docsUrl);
    window.open(docsUrl);
  };

  const currentTabId = activeTabIdProp || activeTabId;
  const isBlocksTab = currentTabId === DevelopmentTool.BLOCKS;

  return (
    <Box direction="vertical" width="100%" gap="24px">
      <Box direction="vertical" width="100%">
        <Heading dataHook={DataHooks.GridTitle} size="large">
          {t(tab.gridTitleKey)}
        </Heading>

        <Box gap="100px" align="space-between">
          <Text dataHook={DataHooks.GridSubtitle}>
            {t(tab.gridSubtitleKey)}
          </Text>
          <TextButton
            dataHook={DataHooks.GridDocs}
            onClick={onDocsLinkClick}
            suffixIcon={<ExternalLink />}
          >
            {t(tab.gridDocsKey)}
          </TextButton>
        </Box>
      </Box>

      {shouldDisplayNotifications && isBlocksTab && (
        <BlocksHarmonyNotification />
      )}
    </Box>
  );
};
